import { useState, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FlatButton } from '@framework/ui/atoms';
import { classNames } from '@framework/utils';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { Link } from 'react-router';
import * as EmailValidator from 'email-validator';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { trpcClient } from '@framework/trpcClient';

type Props = {
  setTemporaryId: (temporaryId: string) => void;
  onClose: () => void;
};

export const LoginWithEmailForm: React.FC<Props> = ({ setTemporaryId, onClose }: Props) => {
  const { t } = useTranslation();
  const [mailAddress, setMailAddress] = useState('');
  const [mailAddressError, setMailAddressError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const isSendable = mailAddress && password && mailAddressError === '' && passwordError === '';

  const handleChangeMailAddress = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value;
    setMailAddress(email);
    if (EmailValidator.validate(email)) {
      setMailAddressError('');
    }
  }, []);
  const handleChangePassword = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  }, []);

  const handleBlurMailAddress = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const email = event.target.value;

      if (EmailValidator.validate(email)) {
        setMailAddressError('');
      } else {
        setMailAddressError(t('auth.login.emailRequired'));
      }
    },
    [t]
  );
  const handleBlurPassword = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (event.target.value === '') {
        setPasswordError(t('auth.login.passwordRequired'));
      } else {
        setPasswordError('');
      }
    },
    [t]
  );

  const handleSubmit = useCallback(async () => {
    if (mailAddress == '' || password == '') {
      return;
    }
    setProcessing(true);
    setError('');

    try {
      const result = await trpcClient.user.auth.loginWithPassword.mutate({ mailAddress, password });
      if (result.ok && 'token' in result) {
        await FirebaseAuthenticationClient.signInWithCustomToken(result.token);
      } else if (result.ok && 'temporaryId' in result) {
        setTemporaryId(result.temporaryId);
      } else {
        setError(t('auth.login.loginFailed'));
      }
    } catch (error) {
      console.error(error);
      setError(t('auth.login.systemError'));
    } finally {
      setProcessing(false);
    }
  }, [mailAddress, password, setTemporaryId, t]);

  return (
    <>
      <div className="flex w-80 flex-col px-5 py-2">
        <div className="cursor-pointer font-bold text-brand hover:underline" onClick={onClose}>
          {t('auth.back.label')}
        </div>

        <div className="pb-2 pt-4 font-bold">{t('auth.login.emailLabel')}</div>
        <input
          type="text"
          value={mailAddress}
          onChange={handleChangeMailAddress}
          onBlur={handleBlurMailAddress}
          className={classNames('w-full rounded-sm border p-2', mailAddressError ? 'border-red-700' : '')}
          autoFocus
          disabled={processing}
        />
        {mailAddressError && <div className="text-sm text-red-700">{mailAddressError}</div>}

        <div className="pb-2 pt-4 font-bold">{t('auth.login.passwordLabel')}</div>
        <input
          type="password"
          value={password}
          onChange={handleChangePassword}
          onBlur={handleBlurPassword}
          className={classNames('w-full rounded-sm border p-2', passwordError ? 'border-red-700' : '')}
          disabled={processing}
        />
        {passwordError && <div className="text-sm text-red-700">{passwordError}</div>}

        {error && <div className="pt-4 text-sm text-red-700">{error}</div>}

        <div className="pt-4" />
        <FlatButton color="brand" onClick={handleSubmit} disabled={!isSendable || processing} loading={processing}>
          {t('auth.login.submit')}
        </FlatButton>
      </div>

      <div className="mt-8">
        {t('auth.login.lockWarning')}
        <br />
        <Trans
          i18nKey="auth.login.lockResetNote"
          components={{
            resetLink: (
              <Link to={UserPagePathBuilder.passwordResetPage()} className="text-sm text-blue-700 hover:underline" />
            ),
          }}
        />
      </div>
    </>
  );
};
